import { Link } from "react-router-dom";
import { ArrowRight, ReceiptIndianRupee, FileBadge, MessageSquareWarning, MapPin, ChevronRight, ShieldCheck, Sparkles } from "lucide-react";
import { CitizenHeader } from "@/components/CitizenHeader";
import { useLang } from "@/context/LangContext";

const HERO_IMG = "https://images.unsplash.com/flagged/photo-1576106035594-69a5d049173f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwxfHxoYXJ5YW5hJTIwYWdyaWN1bHR1cmUlMjBydXJhbCUyMGxhbmRzY2FwZXxlbnwwfHx8fDE3ODEzNDM0MjZ8MA&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  const { t, lang } = useLang();
  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <CitizenHeader />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Haryana rural" className="w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(10,49,97,0.85) 0%, rgba(10,49,97,0.65) 60%, rgba(10,49,97,0.95) 100%)" }} />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-20 sm:py-28 text-white">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/20 text-xs uppercase tracking-[0.2em] mb-6">
              <Sparkles className="w-3.5 h-3.5 text-[#E87722]" />
              <span>{t("tagline")}</span>
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight">
              {t("heroTitle")}
            </h1>
            <p className="mt-6 text-base sm:text-lg text-white/80 max-w-2xl leading-relaxed">
              {t("heroSub")}
            </p>

            <div className="mt-9 flex flex-wrap gap-3">
              <Link to="/login" data-testid="cta-pay-tax" className="inline-flex items-center gap-2 px-5 py-3 rounded-md font-semibold text-sm" style={{ background: "#E87722" }}>
                <ReceiptIndianRupee className="w-4 h-4" /> {t("ctaPayTax")}
              </Link>
              <Link to="/grievance/new" data-testid="cta-grievance" className="inline-flex items-center gap-2 px-5 py-3 rounded-md font-semibold text-sm border border-white/30 hover:bg-white/10">
                <MessageSquareWarning className="w-4 h-4" /> {t("ctaGrievance")}
              </Link>
              <Link to="/track" data-testid="cta-track" className="inline-flex items-center gap-2 px-5 py-3 rounded-md font-semibold text-sm border border-white/30 hover:bg-white/10">
                {t("ctaTrack")} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            {/* Trust strip */}
            <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-2xl">
              {[
                { v: "6,210+", l: lang === "hi" ? "ग्राम पंचायतें" : "Gram Panchayats" },
                { v: "1.7 Cr", l: lang === "hi" ? "नागरिक" : "Citizens" },
                { v: "22", l: lang === "hi" ? "ज़िले" : "Districts" },
                { v: "143", l: lang === "hi" ? "खंड" : "Blocks" },
              ].map((s, i) => (
                <div key={i} className="border-l border-white/20 pl-3">
                  <div className="font-heading text-2xl font-bold">{s.v}</div>
                  <div className="text-[11px] uppercase tracking-[0.18em] text-white/60 mt-1">{s.l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20" data-testid="services-section">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[#E87722] section-rule">{t("services")}</div>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-bold text-slate-900">{t("coreServices")}</h2>
            <p className="mt-2 text-slate-600 max-w-2xl">{t("coreServicesSub")}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { icon: ReceiptIndianRupee, title: t("tax"), desc: t("propertyTaxDesc"), to: "/login", tid: "card-tax" },
            { icon: FileBadge, title: t("certificates"), desc: t("certificateDesc"), to: "/login", tid: "card-cert" },
            { icon: MessageSquareWarning, title: t("grievance"), desc: t("grievanceDesc"), to: "/grievance/new", tid: "card-grievance" },
            { icon: MapPin, title: t("villageInfo"), desc: t("villageInfoDesc"), to: "/login", tid: "card-village" },
          ].map((s, i) => {
            const Icon = s.icon;
            return (
              <Link
                to={s.to}
                key={i}
                data-testid={s.tid}
                className="group block rounded-md border border-slate-200 bg-white p-6 hover:shadow-md hover:-translate-y-0.5 transition-all"
              >
                <div className="w-10 h-10 rounded-md bg-[#0A3161]/5 flex items-center justify-center mb-4 group-hover:bg-[#0A3161] group-hover:text-white transition-colors text-[#0A3161]">
                  <Icon className="w-5 h-5" />
                </div>
                <h3 className="font-heading font-semibold text-lg text-slate-900">{s.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.desc}</p>
                <div className="mt-5 text-sm font-semibold text-[#0A3161] inline-flex items-center gap-1">
                  {t("ctaTrack").split(" ")[0]} <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* About / Promise */}
      <section className="bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 grid grid-cols-1 lg:grid-cols-3 gap-10">
          <div className="lg:col-span-1">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[#E87722] section-rule">{lang === "hi" ? "हमारा वचन" : "Our promise"}</div>
            <h2 className="mt-3 font-heading text-3xl font-bold text-slate-900">
              {lang === "hi" ? "एक प्रणाली। हर सेवा।" : "One platform. Every service."}
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              {lang === "hi"
                ? "HSPS हरियाणा के सभी पंचायतों को एक डिजिटल मंच पर लाता है — आकलन, कर, प्रमाणपत्र, शिकायतें और शासन।"
                : "HSPS unifies every Gram Panchayat in Haryana on a single digital backbone — assessments, taxes, certificates, grievances and governance."}
            </p>
          </div>
          <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              { t: lang === "hi" ? "पारदर्शिता" : "Transparency", d: lang === "hi" ? "हर लेनदेन ऑडिटेबल" : "Every transaction is auditable." },
              { t: lang === "hi" ? "गोपनीयता" : "Privacy", d: lang === "hi" ? "DPDP अनुपालन" : "DPDP compliant by design." },
              { t: lang === "hi" ? "द्विभाषी" : "Bilingual", d: lang === "hi" ? "हिंदी और अंग्रेज़ी" : "Hindi & English everywhere." },
              { t: lang === "hi" ? "तेज़ी" : "Speed", d: lang === "hi" ? "ई-रसीदें तुरंत" : "Instant e-receipts & QR certificates." },
            ].map((b, i) => (
              <div key={i} className="rounded-md border border-slate-200 p-5">
                <ShieldCheck className="w-5 h-5 text-[#0A3161]" />
                <div className="mt-3 font-heading font-semibold text-slate-900">{b.t}</div>
                <div className="mt-1 text-sm text-slate-600">{b.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0A3161] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 grid grid-cols-1 md:grid-cols-3 gap-8">
          <div>
            <div className="font-heading text-lg font-semibold">{t("appName")}</div>
            <div className="text-xs uppercase tracking-[0.2em] text-white/60 mt-1">{t("panchayatiRaj")}</div>
            <p className="text-sm text-white/70 mt-4">{t("digitalSignature")}</p>
          </div>
          <div className="text-sm text-white/80 space-y-2">
            <div className="text-xs uppercase tracking-[0.18em] text-white/50 mb-2">{t("services")}</div>
            <Link className="block hover:text-white" to="/grievance/new">{t("grievance")}</Link>
            <Link className="block hover:text-white" to="/track">{t("track")}</Link>
            <Link className="block hover:text-white" to="/login">{t("login")}</Link>
          </div>
          <div className="text-sm text-white/80">
            <div className="text-xs uppercase tracking-[0.18em] text-white/50 mb-2">Helpline</div>
            <div>1800-XXX-1064</div>
            <div className="text-white/60 text-xs mt-2">© {new Date().getFullYear()} {t("govOfHaryana")}</div>
          </div>
        </div>
      </footer>
    </div>
  );
}
